import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { confirmarSistema } from "../Components/PopupSistema/PopupSistema";
import { db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual, getUsuarioAtualComTenant } from "../Utils/tenant";
import { gerarSequencial } from "../Utils/sequencial";
import RelatorioAnaliseCriticaDirecaoPrint from "./RelatorioAnaliseCriticaDirecaoPrint";

const COLLECTION = "tblAnalisesCriticasDirecao";

function dataHoje() {
  const agora = new Date();
  const local = new Date(agora.getTime() - agora.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

const MODELO = {
  objetivo: "Registrar a análise crítica do SGQ, avaliando sua adequação, eficácia e alinhamento estratégico, além de propor melhorias, novos recursos e oportunidades de inovação.",
  situacaoAcoesAnteriores: "A primeira análise crítica formal do SGQ foi realizada em 17/09/2025. Desde então, as ações e melhorias propostas foram monitoradas e implementadas.",
  mudancasInternas: "Consolidação da matriz de riscos, com classificação e monitoramento dos riscos estratégicos e operacionais.\nAcompanhamento dos indicadores de objetivos da qualidade.\nImplementação de melhorias físicas e estruturais.\nCapacitação contínua da equipe técnica.\nExpansão do SGQ com novos processos e serviços.",
  mudancasExternas: "Clientes exigindo maior rastreabilidade, atuação dos órgãos reguladores, aumento da concorrência e atualização de normas técnicas aplicáveis.",
  mudancasAmbientais: "Análise de impactos ambientais com foco em práticas sustentáveis, reaproveitamento de resíduos e ações de melhoria ambiental.",
  desempenhoEficacia: "Indicadores de Qualidade: avaliar resultados e atendimento das metas.\nNão conformidades e melhorias: avaliar registros, tratamentos e prazos.\nObjetivos da Qualidade: avaliar o alinhamento ao planejamento estratégico.",
  feedbackPartesInteressadas: "Clientes: avaliar satisfação, percepção técnica e oportunidades de melhoria.\nÓrgãos reguladores: avaliar relacionamento e requisitos aplicáveis.\nColaboradores: avaliar clima interno, capacitação e desenvolvimento.\nFornecedores: avaliar desempenho e riscos de fornecimento.",
  conformidadeProdutosServicos: "Avaliar a conformidade dos produtos e serviços, o atendimento aos procedimentos e a confiabilidade dos registros.",
  desempenhoFornecedores: "Avaliar o desempenho dos fornecedores externos e as ações necessárias.",
  adequacaoRecursos: "Avaliar pessoas, infraestrutura, ambiente de trabalho, equipamentos e demais recursos necessários ao SGQ.",
  eficaciaRiscosOportunidades: "Avaliar a eficácia das ações definidas para riscos e oportunidades e a necessidade de novos controles.",
  oportunidadesMelhoria: "Digitalização e integração dos registros do SGQ.\nAprimoramento das práticas de sustentabilidade.\nExpansão estratégica e fortalecimento da imagem institucional.",
  melhoriaSGQProcessos: "Revisar processos e documentos críticos.\nConsolidar a matriz de riscos.\nConsolidar indicadores e auditorias internas.\nRegistrar mudanças e acompanhar ações de melhoria.",
  melhoriaProdutosServicos: "Definir ações para melhoria dos produtos e serviços e atendimento das metas da qualidade.",
  necessidadesRecursos: "Definir recursos humanos, infraestrutura, equipamentos, sistemas e capacitações necessários.",
  alinhamentoEstrategico: "Integrar indicadores ao planejamento estratégico.\nMonitorar periodicamente os KPIs.\nReforçar a cultura de melhoria contínua.",
  documentosReferencia: "Manual da Qualidade.\nProcedimentos Operacionais aplicáveis.\nRegistros da Qualidade relacionados.\nPlanejamento Estratégico.",
  itensAvancados: "Compliance e Ética: avaliar ações e documentos necessários.\nGestão ESG: avaliar métricas ambientais e sociais.\nInovação: avaliar novos serviços, ensaios e projetos.\nSegurança da Informação: avaliar controles, backups e acessos.\nBenchmarking: avaliar referências e oportunidades de mercado.",
  conclusao: "Registrar a conclusão quanto à adequação, suficiência e eficácia do SGQ, incluindo o alinhamento estratégico e as ações propostas.",
};

const CAMPOS_TEXTO = [
  ["situacaoAcoesAnteriores", "2.1 Situação das ações de análises críticas anteriores"],
  ["mudancasInternas", "2.2.1 Mudanças internas"],
  ["mudancasExternas", "2.2.2 Mudanças externas"],
  ["mudancasAmbientais", "2.2.3 Mudanças ambientais"],
  ["desempenhoEficacia", "2.3 Desempenho e eficácia do SGQ"],
  ["feedbackPartesInteressadas", "2.4 Feedback das partes interessadas"],
  ["conformidadeProdutosServicos", "2.5 Conformidade de produtos e serviços"],
  ["desempenhoFornecedores", "2.6 Desempenho de fornecedores externos"],
  ["adequacaoRecursos", "2.7 Adequação de recursos"],
  ["eficaciaRiscosOportunidades", "2.8 Eficácia das ações para lidar com riscos e oportunidades"],
  ["oportunidadesMelhoria", "2.9 Oportunidades de melhoria"],
];

function Secao({ numero, titulo, children }) {
  return (
    <Paper sx={{ border: "1px solid #d7e0ea", mb: 2, overflow: "hidden" }}>
      <Typography fontWeight={700} sx={{ backgroundColor: "#c8e9f5", px: 2, py: 1 }}>{numero}. {titulo}</Typography>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function identificarUsuario(usuario) {
  if (!usuario) return "";
  const nome = String(usuario.nome || "").trim();
  const email = String(usuario.email || "").trim();
  return nome && email ? `${nome} - ${email}` : nome || email;
}

function formatarDataBR(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data || "-";
}

function obterEmailAprovador(registro) {
  const emailSalvo = String(registro?.aprovacaoUsuario?.email || "").trim();
  if (emailSalvo) return emailSalvo;
  return String(registro?.aprovacaoNome || "").match(
    /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/
  )?.[0] || "";
}

export default function DetalhesAnaliseCriticaDirecao() {
  const { registroId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [registro, setRegistro] = useState(null);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoRelatorio, setModoRelatorio] = useState(false);
  const [aprovacaoPersistida, setAprovacaoPersistida] = useState(false);

  useEffect(() => {
    async function carregar() {
      const usuario = await getUsuarioAtualComTenant();
      setUsuarioLogado(usuario);
      setModoRelatorio(false);

      if (registroId === "novo") {
        setAprovacaoPersistida(false);
        setRegistro({
          id: null,
          codigo: "",
          numeroRegistro: null,
          versao: "",
          aprovadoEm: "",
          responsavel: identificarUsuario(usuario),
          dataReuniao: "",
          participantes: "",
          ...Object.fromEntries(Object.keys(MODELO).map((campo) => [campo, ""])),
          aprovadoDirecao: false,
          aprovacaoNome: "",
          aprovacaoData: "",
          aprovacaoUsuario: null,
          validadoEm: null,
        });
        return;
      }

      const snapshot = await getDoc(doc(db, COLLECTION, registroId));
      if (!snapshot.exists()) {
        alert("Relatório de análise crítica não encontrado.");
        history.push("/app/analise-critica-direcao");
        return;
      }
      const dados = { id: snapshot.id, ...snapshot.data() };
      setAprovacaoPersistida(dados.aprovadoDirecao === true);
      setRegistro({ ...MODELO, ...dados });
    }

    carregar();
  }, [registroId, history]);

  const idRegistroCarregado = registro?.id;
  useEffect(() => {
    if (idRegistroCarregado && new URLSearchParams(location.search).get("print") === "1") setModoRelatorio(true);
  }, [location.search, idRegistroCarregado]);

  function atualizarCampo(campo, valor) {
    setMensagem("");
    setRegistro((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarAprovacaoData(data) {
    setMensagem("");
    setRegistro((atual) => ({ ...atual, aprovacaoData: data }));
  }

  function alternarAprovacaoFinal(aprovado) {
    setMensagem("");
    if (aprovado && !registro.aprovacaoData) {
      alert("Informe a data da aprovação antes de confirmar.");
      return;
    }
    setRegistro((atual) => ({
      ...atual,
      aprovadoDirecao: aprovado,
      aprovacaoNome: aprovado ? identificarUsuario(usuarioLogado) : "",
      aprovacaoUsuario: aprovado ? { uid: usuarioLogado?.uid || "", nome: usuarioLogado?.nome || "", email: usuarioLogado?.email || "" } : null,
      validadoEm: aprovado ? new Date() : null,
      aguardandoAprovacao: false,
    }));
  }

  async function solicitarAlteracao() {
    if (!usuarioLogado || !registro.id) return;

    const solicitacaoAlteracao = {
      status: "pendente",
      solicitadoPor: {
        uid: usuarioLogado.uid || "",
        nome: usuarioLogado.nome || "",
        email: usuarioLogado.email || "",
      },
      solicitadoEm: new Date(),
    };

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, registro.id), {
        solicitacaoAlteracao,
        atualizadoEm: new Date(),
      });
      setRegistro((atual) => ({ ...atual, solicitacaoAlteracao }));
      setMensagem("Solicitação de alteração enviada ao usuário que aprovou.");
    } finally {
      setSalvando(false);
    }
  }

  async function decidirSolicitacaoAlteracao(autorizar) {
    const uidAprovador = String(registro.aprovacaoUsuario?.uid || "");
    const emailAprovador = obterEmailAprovador(registro);
    const usuarioEhAprovador =
      (uidAprovador && uidAprovador === usuarioLogado?.uid) ||
      (!uidAprovador && emailAprovador && emailAprovador === usuarioLogado?.email);

    if (!usuarioEhAprovador) {
      alert("Somente o usuário que aprovou pode autorizar esta alteração.");
      return;
    }

    const decididoEm = new Date();
    const solicitacaoAlteracao = {
      ...(registro.solicitacaoAlteracao || {}),
      status: autorizar ? "autorizada" : "negada",
      decididoPor: {
        uid: usuarioLogado.uid || "",
        nome: usuarioLogado.nome || "",
        email: usuarioLogado.email || "",
      },
      decididoEm,
    };
    const alteracoes = autorizar
      ? {
          aprovadoDirecao: false,
          aprovacaoNome: "",
          aprovacaoData: "",
          aprovacaoUsuario: null,
          validadoEm: null,
          historicoAprovacoes: [
            ...(registro.historicoAprovacoes || []),
            {
              nome: registro.aprovacaoNome || "",
              data: registro.aprovacaoData || "",
              usuario: registro.aprovacaoUsuario || null,
              validadoEm: registro.validadoEm || null,
              alteracaoAutorizadaEm: decididoEm,
            },
          ],
        }
      : {};

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, registro.id), {
        ...alteracoes,
        solicitacaoAlteracao,
        atualizadoEm: decididoEm,
      });
      setRegistro((atual) => ({
        ...atual,
        ...alteracoes,
        solicitacaoAlteracao,
        atualizadoEm: decididoEm,
      }));
      if (autorizar) setAprovacaoPersistida(false);
      setMensagem(
        autorizar
          ? "Alteração autorizada. O relatório foi liberado para edição e deverá ser aprovado novamente."
          : "Solicitação de alteração não autorizada."
      );
    } finally {
      setSalvando(false);
    }
  }

  async function salvarRegistro(salvarAprovacao = false) {
    if (aprovacaoPersistida) {
      alert("Este relatório está aprovado e bloqueado para edição.");
      return;
    }
    if (salvarAprovacao === true && registro.aprovadoDirecao !== true) {
      alert("Confirme que leu o relatório completo e aprova esta análise crítica da direção.");
      return;
    }
    if (!String(registro.dataReuniao || "").trim()) {
      alert("Informe a data da reunião.");
      return;
    }
    if (registro.aprovadoDirecao === true && !registro.aprovacaoData) {
      alert("Informe a data da aprovação.");
      return;
    }

    const preenchimentoIncompleto = [
      "versao", "aprovadoEm", "responsavel", "participantes", ...Object.keys(MODELO),
    ].some((campo) => !String(registro[campo] || "").trim());
    if (preenchimentoIncompleto && !(await confirmarSistema("Você não preencheu todo o relatório. Deseja salvar assim mesmo?", {
      titulo: "Relatório com preenchimento incompleto",
      textoConfirmar: "Salvar assim mesmo",
      textoCancelar: "Continuar preenchendo",
    }))) {
      return;
    }

    setSalvando(true);
    try {
      const { id, ...dadosOriginais } = registro;
      const dados = {
        ...dadosOriginais,
        preenchimentoIncompleto,
        aprovacaoNome:
          dadosOriginais.aprovadoDirecao === true
            ? dadosOriginais.aprovacaoNome || identificarUsuario(usuarioLogado)
            : "",
      };
      const atualizadoEm = new Date();
      let idSalvo = id;

      if (!id) {
        const snapshot = await getDocs(consultaTenantAtual(collection(db, COLLECTION)));
        const maiorNumero = snapshot.docs.reduce((maior, item) => Math.max(maior, Number(item.data().numeroRegistro || 0)), 0);
        const sequencial = await gerarSequencial({ chave: "analise_critica_direcao", valorMinimo: maiorNumero });
        const ano = String(dadosOriginais.dataReuniao).slice(0, 4) || String(new Date().getFullYear());
        const dadosCriacao = aplicarTenantAtual({
          ...dados,
          numeroRegistro: sequencial.numero,
          codigo: `ACD-${String(sequencial.numero).padStart(3, "0")}/${ano}`,
          criadoEm: atualizadoEm,
          atualizadoEm,
        });
        const referencia = await addDoc(collection(db, COLLECTION), dadosCriacao);
        idSalvo = referencia.id;
        setRegistro({ id: referencia.id, ...dadosCriacao });
        history.replace(`/app/analise-critica-direcao/${referencia.id}`);
      } else {
        await updateDoc(doc(db, COLLECTION, id), { ...dados, atualizadoEm });
        setRegistro((atual) => ({ ...atual, ...dados, atualizadoEm }));
      }

      setAprovacaoPersistida(dados.aprovadoDirecao === true);
      setMensagem("Relatório salvo com sucesso.");
      if (idSalvo && new URLSearchParams(location.search).get("print") === "1") setModoRelatorio(true);
    } catch (error) {
      console.error("Erro ao salvar análise crítica da direção:", error);
      alert("Não foi possível salvar o relatório.");
    } finally {
      setSalvando(false);
    }
  }

  if (!registro) return null;
  if (modoRelatorio) return <RelatorioAnaliseCriticaDirecaoPrint registro={registro} onVoltar={() => setModoRelatorio(false)} />;

  const edicaoBloqueada = aprovacaoPersistida;
  const solicitacaoAlteracao = registro.solicitacaoAlteracao || {};
  const uidAprovador = String(registro.aprovacaoUsuario?.uid || "");
  const emailAprovador = obterEmailAprovador(registro);
  const usuarioEhAprovador =
    (uidAprovador && uidAprovador === usuarioLogado?.uid) ||
    (!uidAprovador && emailAprovador && emailAprovador === usuarioLogado?.email);

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", mb: 2, p: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Relatório de Análise Crítica do SGQ</Typography>
            <Typography color="text.secondary">RQ 1.5 — {registro.codigo || "Novo relatório"}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push("/app/analise-critica-direcao")}>Voltar</Button>
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setModoRelatorio(true)} disabled={!registro.id}>Relatório</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={salvarRegistro} disabled={salvando || edicaoBloqueada}>{salvando ? "Salvando..." : "Salvar"}</Button>
          </Stack>
        </Paper>

        {mensagem && <Typography color="success.main" fontWeight={700} mb={2}>{mensagem}</Typography>}

        <Box component="fieldset" disabled={edicaoBloqueada} sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}>
          <Secao numero="1" titulo="Identificação e Objetivo">
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Responsável pelo registro" value={registro.responsavel || ""} InputProps={{ readOnly: true }} fullWidth />
                <TextField label="Data da reunião" type="date" InputLabelProps={{ shrink: true }} value={registro.dataReuniao || ""} onChange={(event) => atualizarCampo("dataReuniao", event.target.value)} fullWidth />
                <TextField label="Versão" value={registro.versao || ""} onChange={(event) => atualizarCampo("versao", event.target.value)} sx={{ maxWidth: { md: 140 } }} fullWidth />
                <TextField label="Aprovado em" type="date" InputLabelProps={{ shrink: true }} value={registro.aprovadoEm || ""} onChange={(event) => atualizarCampo("aprovadoEm", event.target.value)} fullWidth />
              </Stack>
              <TextField label="Objetivo" value={registro.objetivo || ""} onChange={(event) => atualizarCampo("objetivo", event.target.value)} multiline minRows={3} fullWidth />
            </Stack>
          </Secao>

          <Secao numero="2" titulo="Estrutura da Reunião - Entradas (ISO 9001:2015, 9.3.2)">
            <Stack spacing={2}>
              {CAMPOS_TEXTO.map(([campo, titulo]) => <TextField key={campo} label={titulo} value={registro[campo] || ""} onChange={(event) => atualizarCampo(campo, event.target.value)} multiline minRows={3} fullWidth />)}
            </Stack>
          </Secao>

          <Secao numero="3" titulo="Saídas da Análise Crítica (ISO 9001:2015, 9.3.3)">
            <Stack spacing={2}>
              <TextField label="Melhoria do SGQ e Processos" value={registro.melhoriaSGQProcessos || ""} onChange={(event) => atualizarCampo("melhoriaSGQProcessos", event.target.value)} multiline minRows={4} fullWidth />
              <TextField label="Melhoria de Produtos e Serviços" value={registro.melhoriaProdutosServicos || ""} onChange={(event) => atualizarCampo("melhoriaProdutosServicos", event.target.value)} multiline minRows={3} fullWidth />
              <TextField label="Necessidades de Recursos" value={registro.necessidadesRecursos || ""} onChange={(event) => atualizarCampo("necessidadesRecursos", event.target.value)} multiline minRows={3} fullWidth />
              <TextField label="Alinhamento Estratégico" value={registro.alinhamentoEstrategico || ""} onChange={(event) => atualizarCampo("alinhamentoEstrategico", event.target.value)} multiline minRows={3} fullWidth />
            </Stack>
          </Secao>

          <Secao numero="4" titulo="Documentos de Referência já Desenvolvidos">
            <TextField value={registro.documentosReferencia || ""} onChange={(event) => atualizarCampo("documentosReferencia", event.target.value)} multiline minRows={4} fullWidth />
          </Secao>

          <Secao numero="5" titulo="Análise de Itens Avançados">
            <Stack spacing={2}>
              <TextField label="Itens avançados" value={registro.itensAvancados || ""} onChange={(event) => atualizarCampo("itensAvancados", event.target.value)} multiline minRows={5} fullWidth />
              <TextField label="Conclusão" value={registro.conclusao || ""} onChange={(event) => atualizarCampo("conclusao", event.target.value)} multiline minRows={5} fullWidth />
              <TextField label="Participantes" value={registro.participantes || ""} onChange={(event) => atualizarCampo("participantes", event.target.value)} placeholder="Separe os nomes por vírgula" fullWidth />
            </Stack>
          </Secao>
        </Box>

        {edicaoBloqueada ? (
          <Paper variant="outlined" sx={{ borderColor: "success.main", p: 2 }}>
            <Typography variant="h6" color="success.main" fontWeight={700}>Relatório aprovado e bloqueado para edição</Typography>
            <Typography>Aprovado por {registro.aprovacaoNome} em {formatarDataBR(registro.aprovacaoData)}.</Typography>

            {solicitacaoAlteracao.status === "pendente" ? (
              <Box mt={2}>
                <Typography color="warning.main" fontWeight={700}>
                  Alteração solicitada por {identificarUsuario(solicitacaoAlteracao.solicitadoPor)}.
                </Typography>
                {usuarioEhAprovador ? (
                  <Stack direction="row" spacing={1} mt={2}>
                    <Button variant="contained" onClick={() => decidirSolicitacaoAlteracao(true)} disabled={salvando}>
                      Autorizar alteração
                    </Button>
                    <Button variant="outlined" color="error" onClick={() => decidirSolicitacaoAlteracao(false)} disabled={salvando}>
                      Não autorizar
                    </Button>
                  </Stack>
                ) : (
                  <Typography color="text.secondary" mt={1}>
                    Aguardando autorização do usuário que aprovou o relatório.
                  </Typography>
                )}
              </Box>
            ) : (
              <Button variant="outlined" onClick={solicitarAlteracao} disabled={salvando} sx={{ mt: 2 }}>
                Solicitar autorização para alterar
              </Button>
            )}
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Validação final da direção</Typography>
            <Typography color="text.secondary" mb={2}>
              Confirme a aprovação somente depois de ler todas as informações do relatório.
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Usuário responsável pela aprovação" value={registro.aprovacaoNome || identificarUsuario(usuarioLogado)} helperText="Nome e e-mail obtidos automaticamente do usuário autenticado." InputProps={{ readOnly: true }} fullWidth />
                <TextField label="Data da aprovação" type="date" InputLabelProps={{ shrink: true }} value={registro.aprovacaoData || ""} onChange={(event) => atualizarAprovacaoData(event.target.value)} fullWidth />
              </Stack>
              <FormControlLabel control={<Checkbox checked={registro.aprovadoDirecao === true} onChange={(event) => alternarAprovacaoFinal(event.target.checked)} />} label="Li o relatório completo e aprovo esta análise crítica da direção." />
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => salvarRegistro(true)} disabled={salvando} sx={{ alignSelf: "flex-end" }}>
                {salvando ? "Salvando..." : "Salvar aprovação"}
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>
    </>
  );
}
